<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUserAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import EditBotSourceForm from './EditBotSourceForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const bot = ref({});
const source = ref({});
const uiFlags = getters['wgptBotSources/getUIFlags'];

const title = computed(() => {
  return (
    t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.EDIT.TITLE') +
    ' ' +
    source.value.name
  );
});

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = (selectedBot, selectedSource) => {
  bot.value = selectedBot;
  source.value = selectedSource;
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const botSource = {
      ...data,
    };
    await store.dispatch('wgptBotSources/update', {
      ...botSource,
      botId: bot.value.id,
      id: source.value.id,
    });

    useAlert(
      t(
        'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.EDIT.API.SUCCESS_MESSAGE'
      )
    );
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.EDIT.API.ERROR_MESSAGE');
    wgptUserAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.EDIT.SAVE')
    "
    :cancel-button-label="
      t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.EDIT.CANCEL')
    "
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    overflow-y-auto
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer class="-top-6 fixed -translate-y-full" />
    <EditBotSourceForm
      v-if="isOpen"
      ref="formRef"
      :source="source"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
